import { CompositorManifest, CompositorRecordingData } from '../types/manifest';
import { LayerSegment, AudioLayerSegment, CompositorLayer, AudioLayer } from '../types/layers';
import { SourceResolver } from './sourceResolver';
import { buildCompositorManifest } from './manifestBuilder';
import { DEFAULT_FPS } from '../constants';

export interface WindowedManifestOptions {
  startMs: number;
  endMs: number;
  fps?: number;
}

interface VideoSegmentFields {
  startFrame: number;
  endFrame: number;
  durationFrames: number;
  tsStartMs: number;
  tsEndMs: number;
  videoStartFrame?: number;
  originalVideoStartMs?: number;
  originalVideoEndMs?: number;
  audioStartFrame?: number;
  originalAudioStartMs?: number;
  originalAudioEndMs?: number;
}

function clampSegmentToWindow<T extends VideoSegmentFields>(
  segment: T,
  windowStartFrame: number,
  windowEndFrame: number,
  fps: number
): T | null {
  if (segment.endFrame <= windowStartFrame || segment.startFrame >= windowEndFrame) {
    return null;
  }

  const clampedStartFrame = Math.max(segment.startFrame, windowStartFrame);
  const clampedEndFrame = Math.min(segment.endFrame, windowEndFrame);
  const offsetStartFrame = clampedStartFrame - windowStartFrame;
  const offsetEndFrame = clampedEndFrame - windowStartFrame;
  const durationFrames = offsetEndFrame - offsetStartFrame;

  if (durationFrames <= 0) return null;

  const clampedStartMs = (clampedStartFrame / fps) * 1000;
  const clampedEndMs = (clampedEndFrame / fps) * 1000;

  // Calculate the offset into the original video/audio file
  // How many frames from the segment's original start to where we want to start playing
  const segmentStartOffsetFrames = clampedStartFrame - segment.startFrame;
  
  // If there was already a videoStartFrame (from previous windowing), add to it
  const existingVideoOffset = segment.videoStartFrame ?? 0;
  const existingAudioOffset = segment.audioStartFrame ?? 0;

  return {
    ...segment,
    startFrame: offsetStartFrame,
    endFrame: offsetEndFrame,
    durationFrames,
    tsStartMs: clampedStartMs - (windowStartFrame / fps) * 1000,
    tsEndMs: clampedEndMs - (windowStartFrame / fps) * 1000,
    // Accumulate the seek offset for video/audio files
    videoStartFrame: existingVideoOffset + segmentStartOffsetFrames,
    audioStartFrame: existingAudioOffset + segmentStartOffsetFrames,
  };
}

export function createWindowedManifest(
  manifest: CompositorManifest,
  options: WindowedManifestOptions
): CompositorManifest {
  const fps = options.fps || manifest.fps;
  const windowStartFrame = Math.floor((options.startMs / 1000) * fps);
  const windowEndFrame = Math.ceil((options.endMs / 1000) * fps);
  const windowDurationFrames = windowEndFrame - windowStartFrame;
  const windowDurationMs = options.endMs - options.startMs;

  const windowedLayers: CompositorLayer[] = manifest.layers.map(layer => {
    const windowedSegments = layer.segments
      .map(segment => clampSegmentToWindow(segment, windowStartFrame, windowEndFrame, fps))
      .filter((seg): seg is LayerSegment => seg !== null);

    return {
      ...layer,
      segments: windowedSegments,
    };
  });

  let windowedAudioLayer: AudioLayer | undefined;
  if (manifest.audioLayer) {
    const windowedAudioSegments = manifest.audioLayer.segments
      .map(segment => clampSegmentToWindow(segment, windowStartFrame, windowEndFrame, fps))
      .filter((seg): seg is AudioLayerSegment => seg !== null);

    if (windowedAudioSegments.length > 0) {
      windowedAudioLayer = {
        ...manifest.audioLayer,
        segments: windowedAudioSegments,
      };
    }
  }

  return {
    ...manifest,
    id: `${manifest.id}-window-${options.startMs}-${options.endMs}`,
    durationMs: windowDurationMs,
    durationFrames: windowDurationFrames,
    layers: windowedLayers,
    audioLayer: windowedAudioLayer,
  };
}

export function createFromSceneWindowedManifest(
  manifest: CompositorManifest,
  clipDurationMs: number
): CompositorManifest {
  const startMs = Math.max(0, manifest.durationMs - clipDurationMs);
  const endMs = manifest.durationMs;
  console.log('[manifestWindowUtils] createFromSceneWindowedManifest:', {
    manifestId: manifest.id,
    manifestDurationMs: manifest.durationMs,
    clipDurationMs,
    windowStartMs: startMs,
    windowEndMs: endMs,
    originalLayerSegmentCounts: manifest.layers.map(l => ({
      type: l.type,
      segmentCount: l.segments.length,
      segments: l.segments.map(s => ({ startFrame: s.startFrame, endFrame: s.endFrame })),
    })),
  });
  const result = createWindowedManifest(manifest, { startMs, endMs });
  console.log('[manifestWindowUtils] createFromSceneWindowedManifest result:', {
    resultId: result.id,
    resultDurationMs: result.durationMs,
    resultDurationFrames: result.durationFrames,
    resultLayerSegmentCounts: result.layers.map(l => ({
      type: l.type,
      segmentCount: l.segments.length,
      segments: l.segments.map(s => ({ startFrame: s.startFrame, endFrame: s.endFrame, durationFrames: s.durationFrames })),
    })),
  });
  return result;
}

export function createToSceneWindowedManifest(
  manifest: CompositorManifest,
  clipDurationMs: number
): CompositorManifest {
  const startMs = 0;
  const endMs = Math.min(clipDurationMs, manifest.durationMs);
  console.log('[manifestWindowUtils] createToSceneWindowedManifest:', {
    manifestId: manifest.id,
    manifestDurationMs: manifest.durationMs,
    clipDurationMs,
    windowStartMs: startMs,
    windowEndMs: endMs,
  });
  const result = createWindowedManifest(manifest, { startMs, endMs });
  console.log('[manifestWindowUtils] createToSceneWindowedManifest result:', {
    resultId: result.id,
    resultDurationMs: result.durationMs,
    resultDurationFrames: result.durationFrames,
    resultLayerSegmentCounts: result.layers.map(l => ({
      type: l.type,
      segmentCount: l.segments.length,
    })),
  });
  return result;
}

export interface BuildWindowedManifestOptions {
  recordingData: CompositorRecordingData;
  pendingBlobs: Map<string, Blob>;
  pendingBlobUrls?: Map<string, string>;
  segmentBlobs?: Map<string, Map<number, Blob>>;
  sourceResolver: SourceResolver;
  fps?: number;
  clipDurationMs: number;
  position: 'from' | 'to';
}

export function buildWindowedManifest(options: BuildWindowedManifestOptions): CompositorManifest | null {
  const {
    recordingData,
    pendingBlobs,
    pendingBlobUrls,
    segmentBlobs,
    sourceResolver,
    fps = DEFAULT_FPS,
    clipDurationMs,
    position,
  } = options;

  try {
    const fullManifest = buildCompositorManifest(recordingData, {
      fps,
      sourceResolver,
      pendingBlobs,
      pendingBlobUrls,
      segmentBlobs,
    });

    if (position === 'from') {
      return createFromSceneWindowedManifest(fullManifest, clipDurationMs);
    } else {
      return createToSceneWindowedManifest(fullManifest, clipDurationMs);
    }
  } catch (error) {
    console.error('[manifestWindowUtils] Failed to build windowed manifest:', error);
    return null;
  }
}
